// payloadParse.ts — PURE classifier/adapter for DECRYPTED TDF payload plaintext.
// CONTRACT:C2-RENDER-ENGINE.1.0
//
// The KAS releases a plaintext whose SHAPE varies by producer (the census):
//   A. frontgate JSON  — {"marking":{…},"fields":{detail,name,theater,source,…}}
//      (services/tak-gateway/src/frontgate.ts; showcase variants add EXTRA fields
//      keys — `fields` is Record<string, unknown>, keys are NOT a closed set);
//   B. live tak-gateway protect() — a PLAIN prose string ("LIVE TAK CONTACT // …");
//   C. seeded objects (scripts/seed-cop-tdf.mjs) — plain prose strings;
//   D. anything else (malformed JSON, marking-less JSON, empty) — must degrade
//      gracefully to prose. FAIL-OPEN ON DISPLAY SHAPE ONLY: an unknown shape
//      renders as text; clearance/decrypt decisions are NEVER made here (this
//      module runs strictly AFTER a KAS grant, on already-released plaintext).
//
// The "structured" acceptance rule MIRRORS kas.ts parseInnerMarking (the single
// existing decider of "does this plaintext self-declare a marking"): trimmed text
// must start with "{", JSON.parse must succeed, and the result must carry an
// object `fields` or a marking with a string `classification`. Never throws.

import type { InnerMarking } from "../host/kas.ts";
import type { HighWater } from "../host/cop.ts";

/** The classified payload shape a renderer can rely on. */
export type ParsedPayload =
  | { kind: "structured"; marking?: InnerMarking; fields: Record<string, unknown> }
  | { kind: "text"; text: string };

/** True iff `v` is a plain (non-null, non-array) object. */
function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** A parseInnerMarking-valid marking (kas.ts:316-327): an object whose
 *  `classification` is a string. Same rule, so the card and the banner-elevation
 *  path can never disagree about whether a payload self-declares a marking. */
function isValidMarking(m: unknown): m is InnerMarking {
  return isPlainObject(m) && typeof m.classification === "string";
}

/** Classify a decrypted plaintext into a renderable shape. Never throws; every
 *  input that is not provably the frontgate `{marking, fields}` JSON degrades to
 *  `{ kind: "text" }` with the plaintext preserved VERBATIM (whitespace intact). */
export function parseSealedPayload(plaintext: string): ParsedPayload {
  const text = typeof plaintext === "string" ? plaintext : "";
  const t = text.trim();
  // Mirror parseInnerMarking's gate: only a JSON OBJECT can be structured.
  if (!t.startsWith("{")) return { kind: "text", text };
  try {
    const o: unknown = JSON.parse(t);
    if (isPlainObject(o)) {
      const fieldsOk = isPlainObject(o.fields);
      const markingOk = isValidMarking(o.marking);
      if (fieldsOk || markingOk) {
        return {
          kind: "structured",
          marking: markingOk ? (o.marking as InnerMarking) : undefined,
          fields: fieldsOk ? (o.fields as Record<string, unknown>) : {},
        };
      }
    }
  } catch {
    /* malformed JSON — an opaque payload, rendered as prose */
  }
  return { kind: "text", text };
}

/** Adapt an InnerMarking to the HighWater aggregate the dod-marking banner
 *  machinery consumes — the cop.ts recomputeBanner mapping (mission→missions;
 *  absent arrays become empty, never undefined). Pure; copies the arrays.
 *  FAIL-OPEN ON SHAPE (never throws): the acceptance gate (isValidMarking /
 *  kas.ts parseInnerMarking) only type-checks `classification`, so a decrypted
 *  producer payload can carry a NON-array mission/caveats/releasability (a bare
 *  string, number, object). `?? []` guards null/undefined but NOT a wrong type —
 *  spreading a non-iterable throws (`… is not iterable`), and spreading a string
 *  explodes into per-character junk. Both would crash this component during React
 *  render (no ErrorBoundary in apps/cop) on the exact untrusted post-grant surface
 *  it exists to display. Coerce each field to a string[] instead. */
function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}
export function markingToHighWater(m: InnerMarking): HighWater {
  return {
    classification: m.classification,
    missions: asStringArray(m.mission),
    caveats: asStringArray(m.caveats),
    releasability: asStringArray(m.releasability),
  };
}

/** Compact one-line stringification for a labeled-grid VALUE cell: strings pass
 *  through, arrays join with ", ", objects render as compact JSON, null/undefined
 *  render the em-dash placeholder. Never throws (circular refs cannot arise from
 *  JSON.parse output, but the guard keeps the promise unconditional). */
export function compactValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map((x) => compactValue(x)).join(", ");
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}
